/// <amd-module name="Wit/QueriesHub/QueryList/Components/AllQueriesTab" />

import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils from "VSS/Core/Util/String";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { css, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Card } from "VSSUI/Card";
import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { BoltListDragEvent, cellFromEvent, IListDropData, ListDragDropBehavior, ListDragImage } from "VSSUI/List";
import { ContextMenuBehavior } from "VSSUI/Menu";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData } from "VSSUI/ZeroData";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { EmptyItem, QueryItem } from "Wit/Common/QueryUtils/Models";
import { getQueryUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import { QueryFavoriteButton } from "Wit/QueriesHub/Common/Components/QueryFavoriteButton";
import { QueryMoreButton } from "Wit/QueriesHub/Common/Components/QueryMoreButton";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";

interface ConfirmMoveDialogState {
    queryItemToMove: ITreeItemEx<QueryItem>;
    sourcePath: string;
    targetItem: ITreeItemEx<QueryItem>;
    targetPath: string;
}

export class AllQueriesTab extends VssComponent {
    private _contextMenuBehavior: ContextMenuBehavior;
    private _listDragDropBehavior: ListDragDropBehavior<ITreeItemEx<QueryItem | EmptyItem>>;
    private _confirmMoveDialogVisible = new ObservableValue(false);
    private _confirmMoveDialogState: ObservableValue<ConfirmMoveDialogState | undefined> = new ObservableValue(undefined);
    private _listService: IQueryListService;
    private _moreButtonRefs: React.RefObject<QueryMoreButton>[] = [];
    private _shortcutService: IVssKeyboardShortcutService;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this._listDragDropBehavior = new ListDragDropBehavior({
            allowedTypes: ["query-item"],
            id: "tree",
            isTree: true,
            onDragStart: this._onDrag,
            onDragOver: this._onDragOver,
            onDrop: this._onDrop,
            renderDragImage: event => {
                const item = event.detail.dataTransfer.data.underlyingItem.data;
                return item.isEmptyFolderContext || item.isLoadingContext ? <></> : <ListDragImage text={item.name} />;
            },
            type: "query-item"
        });
        this._listService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        if (this._listService.queryTree.length === 0) {
            this._listService.getQueriesAsync();
        }
        this._contextMenuBehavior = new ContextMenuBehavior(this._onContextMenu, true);
        this._shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.registerShortcuts();
    }

    private registerShortcuts = (): void => {
        this._shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["z"],
            description: Resources.ToggleFullScreenMode,
            action: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.toggleFullScreenMode();
            }
        });
    }

    public componentWillUnmount() {
        this.unregisterShortcuts();
    }

    private unregisterShortcuts = () => {
        this._shortcutService.removeShortcutGroup(Resources.QueriesHubHeader);
    };

    public render(): JSX.Element {
        const onDismiss = () => {
            this._confirmMoveDialogVisible.value = false;
        };

        return (
            <>
                <Card className="flex-grow bolt-table-card bolt-queries-card" contentProps={{ contentPadding: false }}>
                    <UncheckedObserver queryTree={this._listService.queryTree} isLoading={this._listService.isLoading}>
                        {(observerableProps: { queryTree: TreeItemProvider<QueryItem | EmptyItem>; isLoading: boolean }) => {
                            return observerableProps.isLoading ? (
                                <Spinner
                                    className="flex flex-center queries-loading-spinner queries-minimum-card-height"
                                    size={SpinnerSize.large}
                                    label={Resources.Loading}
                                />
                            ) : this._listService.queryTree.length === 0 ? (
                                <ZeroData
                                    primaryText={Resources.NoQueryResultsText}
                                    imagePath={getAssetLocation(
                                        this.context.pageContext,
                                        "ms.vss-work-web/new-queries-hub-content/Illustrations/general-no-results-found.svg"
                                    )}
                                    imageAltText={Resources.NoQueryResultsText}
                                ></ZeroData>
                            ) : (
                                <Tree
                                    ariaLabel={Resources.AllTabName}
                                    behaviors={[this._listDragDropBehavior, this._contextMenuBehavior]}
                                    columns={[this.titleColumn, this.favoriteColumn, this.moreColumn, this.lastModifiedColumn]}
                                    itemProvider={this._listService.queryTree}
                                    showLines={true}
                                    singleClickActivation={false}
                                    onToggle={(event, treeItem: ITreeItemEx<QueryItem>) => {
                                        this._toggleTreeItem(treeItem);
                                    }}
                                    scrollable={false}
                                />
                            );
                        }}
                    </UncheckedObserver>
                </Card>
                <Observer isDialogOpen={this._confirmMoveDialogVisible} queryItems={this._confirmMoveDialogState}>
                    {(props: { isDialogOpen: boolean; queryItems: ConfirmMoveDialogState | undefined }) => {
                        return props.isDialogOpen ? (
                            <Dialog
                                titleProps={{ text: "Confirm" }}
                                footerButtonProps={[
                                    {
                                        text: Resources.Cancel,
                                        onClick: onDismiss
                                    },
                                    {
                                        text: Resources.Move,
                                        onClick: () => {
                                            this._moveQuery(props.queryItems!.queryItemToMove, props.queryItems!.targetItem);
                                            onDismiss();
                                        },
                                        primary: true
                                    }
                                ]}
                                onDismiss={onDismiss}
                            >
                                {Utils.format(Resources.QueryConfirmDragDropOperation, props.queryItems!.sourcePath, props.queryItems!.targetPath)}
                            </Dialog>
                        ) : null;
                    }}
                </Observer>
            </>
        );
    }

    private _confirmMoveQueryItemDialog = (queryItemToMove: ITreeItemEx<QueryItem>, targetItem: ITreeItemEx<QueryItem>): void => {
        const sourcePath = this._getRootDirectory(queryItemToMove.underlyingItem.data.path) || queryItemToMove.underlyingItem.data.path;
        const targetPath = this._getRootDirectory(targetItem.underlyingItem.data.path) || targetItem.underlyingItem.data.path;
        this._confirmMoveDialogState.value = { queryItemToMove, sourcePath, targetItem, targetPath };
        this._confirmMoveDialogVisible.value = true;
    };

    private _getRootDirectory = (path: string): string => {
        return path ? path.split("/")[0] : path;
    };

    private _moveQuery = async (originalQueryItem: ITreeItemEx<QueryItem>, targetTreeItem: ITreeItemEx<QueryItem>): Promise<void> => {
        this._listService.moveQuery(originalQueryItem, targetTreeItem.underlyingItem);
    };

    private _toggleTreeItem = (treeItem: ITreeItemEx<QueryItem>) => {
        this._listService.toggleTreeItem(treeItem);
    };

    private _onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        const rowIndex = cellFromEvent(event).rowIndex;
        if (this._moreButtonRefs[rowIndex]?.current) {
            this._moreButtonRefs[rowIndex]!.current!.expand();
            event.preventDefault();
        }
    };

    private _onDrag = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<QueryItem>>) => {
        const item = event.detail.dataTransfer.data;
        if (item.depth === 0 || item.underlyingItem.data.isEmptyFolderContext) {
            event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
        }
    };

    private _onDragOver = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<QueryItem>>, dropData: IListDropData) => {
        const targetTreeItem = this._listService.queryTree.value.find((element: ITreeItemEx<QueryItem | EmptyItem>, index: number) => {
            // find the element at the target index
            return index === dropData.index;
        });
        if (
            !targetTreeItem ||
            targetTreeItem.underlyingItem.data.isEmptyFolderContext ||
            !targetTreeItem.underlyingItem.data.isFolder ||
            targetTreeItem.underlyingItem.data.id === event.detail.dataTransfer.data.parentItem?.underlyingItem.data.id
        ) {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }
    };

    private _onDrop = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<QueryItem>>, dropData: IListDropData) => {
        const item = event.detail.dataTransfer.data.underlyingItem;
        const targetTreeItem = this._listService.queryTree.value.find((element: ITreeItemEx<QueryItem | EmptyItem>, index: number) => {
            // find the element at the target index
            return index === dropData.index;
        });
        if (item && targetTreeItem && !targetTreeItem.underlyingItem.data.isEmptyFolderContext && targetTreeItem.underlyingItem.data.isFolder) {
            if (item.data.isPublic && !targetTreeItem.underlyingItem.data.isPublic) {
                this._confirmMoveQueryItemDialog(event.detail.dataTransfer.data, targetTreeItem as ITreeItemEx<QueryItem>);
            } else {
                this._moveQuery(event.detail.dataTransfer.data, targetTreeItem as ITreeItemEx<QueryItem>);
            }
        }
    };

    private _onSize = (event: MouseEvent, index: number, width: number, column: ITreeColumn<QueryItem>) => {
        (column.width as ObservableValue<number>).value = width;
    };

    private _renderTreeCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<QueryItem>,
        treeItem: ITreeItemEx<QueryItem>
    ): JSX.Element => {
        const item = treeItem.underlyingItem.data;
        if (!this._moreButtonRefs[rowIndex]) {
            this._moreButtonRefs[rowIndex] = React.createRef<QueryMoreButton>();
        }

        let content: JSX.Element;
        const queryTypeLabel = QueryUtils.getQueryTypeShortText(item.queryType, item.isInvalidSyntax);
        const iconName = item.isFolder ? "FabricFolderFill" : QueryUtils.getQueryTypeIcon(item.queryType);
        const icon = (
            <Tooltip text={queryTypeLabel}>
                <Icon className={css("margin-right-8", item.isFolder ? "queries-folder-icon" : undefined)} iconName={iconName} />
            </Tooltip>
        );
        if (item.isLoadingContext) {
            content = <Spinner size={SpinnerSize.medium} />;
        } else if (item.isEmptyFolderContext) {
            content = (
                <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                    <Tooltip overflowOnly={true}>
                        <span className="secondary-text text-ellipsis">{Resources.EmptyFolder}&nbsp;</span>
                    </Tooltip>
                </span>
            );
        } else {
            content = (
                <div className="flex-row flex-grow text-ellipsis">
                    <span className="bolt-list-cell-child flex-row flex-center bolt-list-cell-text">
                        {icon}
                        <span className="inline-flex-row scroll-hidden">
                            <Tooltip overflowOnly={true}>
                                {item.isFolder ? (
                                    <span className="text-ellipsis">{item.name}</span>
                                ) : (
                                        <Link
                                            className="text-ellipsis"
                                            href={getQueryUrl(this.context.pageContext, item.id)}
                                            subtle={true}
                                            removeUnderline={true}
                                        >
                                            {item.name}
                                        </Link>
                                )}
                            </Tooltip>
                        </span>
                    </span>
                </div>
            );
        }

        return ExpandableTreeCell({
            children: content,
            columnIndex,
            contentClassName: undefined,
            treeColumn,
            treeItem
        });
    };

    private _renderFavoriteCell = (rowIndex: number, columnindex: number, treeColumn: ITreeColumn<QueryItem>, treeItem: ITreeItemEx<QueryItem>) => {
        const item = treeItem.underlyingItem.data;
        const content = (
            <SimpleTableCell columnIndex={columnindex} key={"favorite-cell" + rowIndex}>
                <QueryFavoriteButton
                    className={"queries-hub-favorite-cell"}
                    item={item}
                    onAddFavorite={() => this._listService.addFavorite(item)}
                    onRemoveFavorite={() => this._listService.removeFavorite(item)}
                />
            </SimpleTableCell>
        );

        return item.isLoadingContext || item.isEmptyFolderContext || item.isFolder ? (
            <SimpleTableCell columnIndex={columnindex} key={"empty-favorite" + rowIndex} />
        ) : (
            content
        );
    };

    private _renderMoreCell = (rowIndex: number, columnindex: number, treeColumn: ITreeColumn<QueryItem>, treeItem: ITreeItemEx<QueryItem>) => {
        const item = treeItem.underlyingItem.data;
        return !item.isLoadingContext && !item.isEmptyFolderContext ? (
            <SimpleTableCell contentClassName="bolt-list-cell-content-reveal" columnIndex={columnindex} key={"more-cell" + item.id}>
                <QueryMoreButton item={item as QueryItem} ref={this._moreButtonRefs[rowIndex]} />
            </SimpleTableCell>
        ) : (
            <SimpleTableCell columnIndex={columnindex} key={"empty-column-more" + rowIndex} />
        );
    };

    private _renderModifiedCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<QueryItem>,
        treeItem: ITreeItemEx<QueryItem>
    ): JSX.Element => {
        if (
            treeItem.underlyingItem.data.isEmptyFolderContext ||
            treeItem.underlyingItem.data.isLoadingContext ||
            !treeItem.underlyingItem.data.lastModifiedBy
        ) {
            return <SimpleTableCell ariaRowIndex={rowIndex} columnIndex={columnIndex} key={"col-" + columnIndex + " row-" + rowIndex} />;
        }
        const identity = treeItem.underlyingItem.data.lastModifiedBy;
        const dayInSeconds = 60 * 60 * 24;
        const text = Utils.format(
            Resources.Updated,
            identity.displayName,
            QueryUtils.getFriendlyDate(treeItem.underlyingItem.data.lastModifiedDate, dayInSeconds)
        );

        const imageUrl = getIdentityImageUrl(this.context.pageContext, identity);
        return (
            <SimpleTableCell
                ariaRowIndex={rowIndex}
                contentClassName="text-ellipsis"
                columnIndex={columnIndex}
                key={`col-${columnIndex} row-${rowIndex}`}
            >
                <VssPersona cssClass={"margin-right-8"} displayName={identity.displayName} imageUrl={imageUrl} size="extra-small" />
                <Tooltip overflowOnly={false} text={text}>
                    <span className="text-ellipsis">{text}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private titleColumn: ITreeColumn<QueryItem> = {
        id: "title",
        name: Resources.TitleColumn,
        width: new ObservableValue(-100),
        renderCell: this._renderTreeCell,
        onSize: this._onSize
    };
    private favoriteColumn: ITreeColumn<QueryItem> = {
        ariaLabel: Resources.Favorite,
        id: "favorite-column",
        readonly: true,
        renderCell: this._renderFavoriteCell,
        width: 32 + QueryUtils.getColumnHeaderPadding()
    };
    private moreColumn: ITreeColumn<QueryItem> = {
        ariaLabel: Resources.ContextMenuColumn,
        id: "more-button-column",
        readonly: true,
        renderCell: this._renderMoreCell,
        width: 32 + QueryUtils.getColumnHeaderPadding()
    };
    private lastModifiedColumn: ITreeColumn<QueryItem> = {
        id: "last-modified",
        name: Resources.LastModifiedColumn,
        width: new ObservableValue(-70),
        renderCell: this._renderModifiedCell,
        onSize: this._onSize
    };
}
